<template>
  <v-container fluid class="auth-wrapper pa-0">
    <v-row no-gutters class="fill-height min-vh-100">
      <!-- Left Side: Visual Pane (Same as Login for continuity) -->
      <v-col cols="12" md="6" class="visual-pane d-none d-md-flex align-center justify-center">
        <div class="visual-content pa-12">
          <v-img
            max-width="220"
            class="mb-12"
            src="@/assets/runflow_logo.png"
            contain
          ></v-img>
          <div class="brand-message">
            <h1 class="text-h3 font-weight-bold white--text mb-6 raleway-font tracking-tight">
              Recuperar <span class="accent-text">Acceso</span>.
            </h1>
            <p class="text-h6 white--text opacity-70 lato-font font-weight-light lh-relaxed">
              Te enviaremos las instrucciones necesarias para <br>restablecer tu contraseña de forma segura.
            </p>
          </div>
        </div>
      </v-col>

      <!-- Right Side: Request Form -->
      <v-col cols="12" md="6" class="form-pane d-flex align-center justify-center">
        <div class="auth-card-container pa-8 pa-sm-16">
          
          <!-- State 1: Request Email -->
          <div v-if="!sent" class="text-center">
            <div v-if="logo" class="company-logo-login-container mb-8 pa-4 d-inline-block">
              <v-img :src="logo" contain max-height="80" max-width="180" class="mx-auto"></v-img>
            </div>
            <div class="mb-10 text-left">
              <v-btn to="/auth/login" text depressed small class="px-0 mb-6 accent-text font-weight-bold lato-font">
                <v-icon left size="14">fas fa-arrow-left</v-icon> Volver al inicio
              </v-btn>
              <h2 class="text-h4 font-weight-bold slate-text raleway-font mb-2">¿Olvidaste tu contraseña?</h2>
              <p class="grey--text text--darken-1 lato-font font-weight-medium">Ingresa tu correo electrónico institucional</p>
            </div>

            <v-form @submit.prevent="sendEmail">
              <div class="input-group mb-8">
                <label class="input-label mb-2 d-block slate-text font-weight-bold text-uppercase caption tracking-wider">Correo Electrónico</label>
                <v-text-field
                  v-model="email"
                  placeholder="ejemplo@empresa.com"
                  outlined
                  flat
                  dense
                  class="modern-input-field"
                  prepend-inner-icon="mdi-email-outline"
                  type="email"
                  required
                  hide-details="auto"
                  color="#35BDC8"
                ></v-text-field>
              </div>

              <v-btn 
                color="#35BDC8" 
                large 
                block 
                depressed
                type="submit" 
                :loading="loading"
                :disabled="loading"
                class="h-52 rounded-md white--text font-weight-bold text-uppercase tracking-widest login-btn-enterprise"
              >
                Enviar instrucciones
                <template v-slot:loader>
                  <v-progress-circular indeterminate size="22" width="2" color="white"></v-progress-circular>
                </template>
              </v-btn>
            </v-form>
          </div>

          <!-- State 2: Success Message -->
          <div v-else-if="success" class="text-center">
            <v-avatar size="80" color="rgba(76, 175, 80, 0.1)" class="mb-8">
              <v-icon size="36" color="#4CAF50">fas fa-paper-plane</v-icon>
            </v-avatar>
            <h2 class="text-h4 font-weight-bold slate-text raleway-font mb-4">Correo Enviado</h2>
            <p class="grey--text text--darken-1 body-1 lato-font mb-10">
              Hemos enviado un enlace seguro a <strong>{{ email }}</strong>. Por favor revisa tu bandeja de entrada y spam.
            </p>
            <v-btn to="/auth/login" color="#35BDC8" block large depressed class="h-52 rounded-md white--text font-weight-bold text-uppercase tracking-widest">
              Volver al Login
            </v-btn>
          </div>

          <!-- State 3: Error Message -->
          <div v-else class="text-center">
            <v-avatar size="80" color="rgba(244, 67, 54, 0.1)" class="mb-8">
              <v-icon size="36" color="#F44336">fas fa-exclamation-triangle</v-icon>
            </v-avatar>
            <h2 class="text-h4 font-weight-bold error--text raleway-font mb-4">Error de Solicitud</h2>
            <p class="grey--text text--darken-1 body-1 lato-font mb-10">
              No pudimos procesar tu solicitud en este momento. Verifica el correo o contacta a soporte técnico.
            </p>
            <v-btn @click="sent = false" color="#1A1F2E" block large depressed class="h-52 rounded-md white--text font-weight-bold text-uppercase tracking-widest">
              Intentar de nuevo
            </v-btn>
          </div>

          <div class="mt-16 pt-8 text-center border-top-light">
            <p class="caption grey--text text--darken-1 mb-0 lato-font">
              &copy; {{ new Date().getFullYear() }} <strong>Runflow Evolution 9</strong>. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { passwordResetSendEmail, getLogo } from '@/api/auth'

export default {
  name: 'PasswordResetEmail',
  data: () => ({
    loading: false,
    email: '',
    sent: false,
    success: true,
    logo: ''
  }),
  async created() {
    try {
      this.logo = await getLogo()
    } catch (e) {
      console.error(e)
    }
  },
  methods: {
    async sendEmail() {
      if (!this.email) return
      this.loading = true
      try {
        await passwordResetSendEmail(this.email)
        this.success = true
      } catch (e) {
        this.success = false
      } finally {
        this.loading = false
        this.sent = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Raleway:wght@600;700&family=Lato:wght@300;400;700&display=swap');

.raleway-font { font-family: 'Raleway', sans-serif !important; }
.lato-font { font-family: 'Lato', sans-serif !important; }

.auth-wrapper {
  min-height: 100vh;
  background-color: #F8FAFC;
  margin: -12px;
}

.min-vh-100 {
  min-height: 100vh !important;
}

.visual-pane {
  background-color: #1A1F2E;
  position: relative;
}

.visual-content {
  z-index: 1;
  max-width: 520px;
}

.accent-text { color: #35BDC8 !important; }
.slate-text { color: #1A1F2E; }

.company-logo-login-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.04);
  border: 1px solid #F1F5F9;
}

.tracking-tight { letter-spacing: -1px !important; }
.tracking-widest { letter-spacing: 1.5px !important; }
.tracking-wider { letter-spacing: 0.8px !important; }
.lh-relaxed { line-height: 1.6 !important; }
.opacity-70 { opacity: 0.7; }

.form-pane {
  background-color: #FFFFFF;
}

.auth-card-container {
  width: 100%;
  max-width: 520px;
}

.modern-input-field {
  ::v-deep .v-input__control {
    .v-input__slot {
      border-radius: 8px !important;
      background-color: #F8FAFC !important;
      border: 1px solid #E0E0E0 !important;
      box-shadow: none !important;
      transition: all 0.2s ease;
      
      fieldset { border: none; }
    }
    
    &.v-input--is-focused .v-input__slot {
      background-color: #FFFFFF !important;
      border-color: #35BDC8 !important;
      box-shadow: 0 0 0 3px rgba(53, 189, 200, 0.1) !important;
    }
  }
}

.h-52 { height: 52px !important; }
.rounded-md { border-radius: 8px !important; }

.login-btn-enterprise {
  transition: all 0.2s ease;
  box-shadow: none !important;
  &:hover {
    background-color: #2da1ac !important;
    transform: translateY(-1px);
  }
}

.border-top-light { border-top: 1px solid #E0E0E0; }

@media (max-width: 960px) {
  .auth-card-container {
    max-width: 100%;
  }
}
</style>